package com.siriusxm.pia.views.unifiedaggregator.management

import androidx.compose.runtime.*
import com.siriusxm.cmc.error
import com.siriusxm.cmc.success
import com.siriusxm.cmc.utils.encodeURIComponent
import com.siriusxm.pia.components.*
import com.siriusxm.pia.rememberCapability
import com.siriusxm.pia.rest.unifiedaggregator.getConsumer
import com.siriusxm.pia.views.unifiedaggregator.AggregatorService
import com.siriusxm.pia.views.unifiedaggregator.AggregatorStyles
import com.siriusxm.unifiedcontent.console.aggregator.rememberCapabilities
import unifiedcontent.aggregator.AuditResourceType
import unifiedcontent.aggregator.ConsumerDetails
import unifiedcontent.aggregator.ConsumerTarget
import unifiedcontent.aggregator.Contact
import unifiedcontent.aggregator.TargetType
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.ATarget
import org.jetbrains.compose.web.attributes.target
import org.jetbrains.compose.web.css.marginBottom
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Text

/**
 * The view for browsing and editing consumers.
 * Displays a list of consumers with options to filter disabled items.
 * Users authorized for ConsumerCreate can add new consumers via the action button.
 * A settings icon in the top-right allows toggling visibility of disabled consumers.
 */
@Composable
fun ServiceViewContext.consumers(aggregator: AggregatorService, selectedId: String? = null) {
    val consumers = aggregator.consumers
    val selected = consumers.find { it.id == selectedId }
    // Creation is uncommon, so resolve it only on the list screen where the action can appear.
    // Capabilities caches the resulting action-only query if this screen is revisited.
    val canCreateConsumer = selectedId == null &&
            aggregator.context.rememberCapability(ConsumerCreateCapability).value

    // Settings state for show disabled toggle
    var settings by remember { mutableStateOf(ProducerConsumerSettings()) }

    // Reload consumers when settings change
    LaunchedEffect(settings.showDisabled) {
        aggregator.app.reloadConsumers(settings.showDisabled)
    }

    serviceContentView({
        title = "Consumers"

        // Settings icon (top-right) that opens a dialog for view options
        settings {
            producerConsumerSettingsDialog(settings, "consumers") { newSettings ->
                settings = newSettings
            }
        }

        breadcrumbs {
            crumb("Aggregator", "aggregator") {
                crumb("Consumers", "consumers") {
                    if (selected != null) {
                        crumb(selected.name ?: selected.id, null)
                    }
                }
            }
        }
        if (canCreateConsumer) {
            this.action {
                this.primary = true
                this.title = "New Consumer"
                this.action {
                    aggregator.navigate("consumers/edit")
                }
            }
        }
    }) {
        splitSelectionView<ConsumerDetails> {
            items = consumers
            selection = selected
            onSelect {
                aggregator.navigate("consumers/${encodeURIComponent(it.id)}")
            }

            render { consumer ->
                consumer(aggregator, consumer)
            }

            renderListItem {
                buildString {
                    append(it.name ?: it.id)
                    if (it.disabled == true) {
                        append(" (Disabled)")
                    }
                }
            }
        }
    }
}

/**
 * The detail view for a single consumer.
 * Users authorized for ConsumerUpdate can edit, disable, or enable the consumer.
 * A consumer cannot be disabled if it has any active event targets.
 */
@Composable
fun consumer(aggregator: AggregatorService, consumer: ConsumerDetails) {
    var loadedConsumer by remember { mutableStateOf<ConsumerDetails?>(null) }
    var showDisableDialog by remember { mutableStateOf(false) }
    var showEnableDialog by remember { mutableStateOf(false) }
    val scope = rememberCoroutineScope()
    val canUpdateConsumer by aggregator.context.rememberCapability(
        consumerUpdateCapability(consumer.id)
    )

    // Whether the viewer may add targets to this consumer, asked of AVP once per configured account
    // with the same consumerId/targetAccountId context the catalog API checks (UCA-1240). Hide-only:
    // the API re-checks every add and remove. Re-asked when the consumer or its accounts change.
    val consumerAccounts = (loadedConsumer ?: consumer).accounts.orEmpty()
    val addProbes = consumerTargetAddProbes(consumer.id, consumerAccounts)
    val addDecisions by aggregator.context.rememberCapabilities(addProbes)
    val canAddTargets = addDecisions?.values?.any { it } == true

    suspend fun loadConsumer() {
        loadedConsumer = aggregator.clientApi.getConsumer(consumer.id)
    }

    LaunchedEffect(consumer.id) {
        loadedConsumer = null
        loadConsumer()
    }

    // Check if consumer can be disabled (no active event targets)
    // Use loadedConsumer for the full targets data, fall back to consumer if not yet loaded
    val hasEventTargets = (loadedConsumer?.targets ?: consumer.targets)?.isNotEmpty() == true

    entityView({
        title = consumer.name

        if (canUpdateConsumer) {
            menu {
                subAction {
                    title = "Edit"
                    action {
                        aggregator.navigate("consumers/${encodeURIComponent(consumer.id)}/edit")
                    }
                }

                if (consumer.disabled == true) {
                    subAction {
                        title = "Enable"
                        action {
                            showEnableDialog = true
                        }
                    }
                } else {
                    subAction {
                        title = "Disable"
                        action {
                            if (hasEventTargets) {
                                aggregator.notifications.error("This consumer cannot be disabled because it has active event targets. Remove all targets before disabling.")
                            } else {
                                showDisableDialog = true
                            }
                        }
                    }
                }
            }
        }
    }) {
        // Show disabled status badge if disabled
        if (consumer.disabled == true) {
            Div({
                style {
                    marginBottom(16.px)
                }
            }) {
                Badge("DISABLED", BadgeColor.RED)
            }
        }

        // Disable confirmation dialog
        if (showDisableDialog) {
            DisableConfirmationDialog(
                entityType = "consumer",
                entityName = consumer.name,
                entityId = consumer.id
            ) { confirmed ->
                if (confirmed) {
                    scope.launch {
                        try {
                            aggregator.updateConsumer(consumer.copy(disabled = true))
                            aggregator.notifications.success("Consumer disabled successfully")
                        } catch (e: Exception) {
                            aggregator.notifications.error("Failed to disable consumer: ${e.message}")
                        }
                    }
                }
                showDisableDialog = false
            }
        }

        // Enable confirmation dialog
        if (showEnableDialog) {
            EnableConfirmationDialog(
                entityType = "consumer",
                entityName = consumer.name
            ) { confirmed ->
                if (confirmed) {
                    scope.launch {
                        try {
                            aggregator.updateConsumer(consumer.copy(disabled = false))
                            aggregator.notifications.success("Consumer enabled successfully")
                        } catch (e: Exception) {
                            aggregator.notifications.error("Failed to enable consumer: ${e.message}")
                        }
                    }
                }
                showEnableDialog = false
            }
        }
        // The detail box and the tab control share this container's 20px gap; boxes own no
        // outer margin of their own.
        Column(gap = 20.px) {
        box({
            title = "Consumer Details"
        }) {
            detailGrid {
                detail("ID", consumer.id)

                consumer.clientId?.let {
                    detail("OAuth ClientId", it)
                }

                consumer.serviceRegistryId?.let { serviceId ->
                    detail("Service Registry") {
                        A(href = "#service-registry/services/${encodeURIComponent(serviceId)}") {
                            Text("View in Registry")
                        }
                    }
                }

                if (consumer.accounts?.isNotEmpty() == true) {
                    detail("Accounts") {
                        consumer.accounts?.forEach {
                            Div {
                                Text(it)
                            }
                        }
                    }
                }

                (loadedConsumer?.slackChannel ?: consumer.slackChannel)?.let {
                    detail("Slack") {
                        A(
                            href = "https://siriusxm-pandora.slack.com/channels/${it}",
                            { target(ATarget.Blank) }) {
                            Img(src = "images/slack.png") {
                                classes(AggregatorStyles.slackIcon)
                            }
                            Text(it)
                        }
                    }
                }
            }
        }

        // Wrap the tab control in a single Div so the Column(gap) above treats it as one flex
        // item and doesn't insert space between the tab bar and the tab content.
        Div {
        tabView {
            tab("Details") {
                Column(gap = 20.px) {
                if (!consumer.types.isNullOrEmpty()) {
                    box({
                        title = "Consumed Types"
                        paddedContent = false
                    }) {
                        table<String> {
                            items(consumer.types?.toList())

                            column {
                                content { type ->
                                    val entityType = aggregator.entityType(type)
                                    val displayName = entityType?.name?.takeIf { it.isNotBlank() } ?: type
                                    A(href = "#aggregator/types/$type") {
                                        Text(displayName)
                                    }
                                }
                            }
                        }
                    }
                }

                if (!consumer.targets.isNullOrEmpty()) {
                    box({
                        title = "Targets"
                        paddedContent = false
                    }) {
                        table<ConsumerTarget> {
                            items(consumer.targets.orEmpty())

                            column {
                                title = "Type"
                                width = 75.px
                                content { target ->
                                    Text(target.type?.name ?: TargetType.Events.name)
                                }
                            }
                            column {
                                title = "Target"
                                content { target ->
                                    consumerTargetDisplay(target)
                                }
                            }
                        }
                    }
                }

                if (!consumer.contact.isNullOrEmpty()) {
                    box({
                        title = "Contacts"
                        paddedContent = false
                    }) {
                        table<Contact> {
                            items(consumer.contact?.toList())
                            column {
                                content {
                                    Div {
                                        A(href = "mailto:${it.email}") {
                                            Text(it.email)
                                        }
                                    }
                                }
                            }
                        }
                    }
                }

                // The UCP team that owns this consumer; membership is administered in UCP (UCA-1212).
                val teamId = loadedConsumer?.teamId ?: consumer.teamId
                TeamBox(aggregator, teamId)

                // A consumer linked to a team has its members in UCP, so the local agents are not shown.
                if (teamId.isNullOrBlank()) {
                    ServiceUsersBox(aggregator, consumer.id, ServiceUsersType.CONSUMER)
                }

                loadedConsumer?.let {
                    if (canAddTargets || !loadedConsumer?.targets.isNullOrEmpty()) {
                        consumerTargetsView(aggregator, it, canAddTargets) {
                            loadedConsumer = it
                        }
                    }
                }
                }
            }

            tab("History") {
                AuditHistory(aggregator, AuditResourceType.CONSUMER, consumer.id)
            }
        }
        }
        }
    }
}
